import { FAMILY_LABEL } from '../data/families.js';
// Advanced lookup is a superset of the Classic plants (same names)
// plus the storage plants, so summaries name plants in both modes.
import { ADVANCED_PLANT_BY_ID as PLANT_BY_ID } from '../data/advancedCapacity.js';
import { DISASTER_BY_ID } from '../data/disasters.js';
import { COUNTRY_BY_CODE } from '../data/countries.js';
import { DEFAULT_GAME_MODE, GAME_VERSION_BY_MODE } from '../data/modes.js';
import { ROLE_LABEL } from '../state/pages.js';

export const SCORE_ENDPOINT = '/.netlify/functions/scores';
export const GAME_VERSION = 'v2.4';
export const DEFAULT_SCORE_LIMIT = 10;
export const MAX_SCORE_LIMIT = 50;
export const MAX_PORTFOLIO_SUMMARY_LENGTH = 240;
export const MAX_SUBMISSION_KEY_LENGTH = 180;

const COP31_OUTCOME_LABEL = {
  collapse: 'Collapse',
  modest: 'Modest',
  ambitious: 'Ambitious'
};

function clampLimit(limit) {
  const value = Number.parseInt(limit ?? '', 10);
  if (!Number.isFinite(value)) return DEFAULT_SCORE_LIMIT;
  return Math.min(Math.max(value, 1), MAX_SCORE_LIMIT);
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function requestError(response, payload) {
  if (response.status === 404) {
    return new Error('High scores are unavailable. Run with Netlify Dev to test submissions locally.');
  }
  return new Error(
    payload?.error ?? `High scores request failed with status ${response.status}.`
  );
}

function assertFetcher(fetcher) {
  if (typeof fetcher !== 'function') {
    throw new Error('High scores are unavailable.');
  }
}

export function formatCop31Outcome(outcome) {
  return COP31_OUTCOME_LABEL[outcome] ?? 'Unknown';
}

export function formatPlayerRole(role) {
  return ROLE_LABEL[role] ?? role ?? 'Unspecified role';
}

export function formatPlayerCountry(countryCode) {
  return COUNTRY_BY_CODE[countryCode] ?? countryCode ?? 'Unspecified country';
}

export function formatCountryFlag(countryCode) {
  const code = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...[...code].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65)
  );
}

function cleanScoreText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function createPortfolioSummary(portfolio = []) {
  const counts = new Map();
  const families = new Set();

  for (const id of portfolio) {
    const plant = PLANT_BY_ID[id];
    if (!plant) continue;
    const current = counts.get(id);
    counts.set(id, {
      plant,
      count: current ? current.count + 1 : 1
    });
    families.add(plant.family);
  }

  const entries = [...counts.values()];
  const plantCount = entries.reduce((sum, entry) => sum + entry.count, 0);
  if (plantCount === 0) return 'No plants built';

  const plantText = plantCount === 1 ? 'plant' : 'plants';
  const familyText = families.size === 1 ? 'family' : 'families';
  const summary = entries
    .map(({ plant, count }) => `${plant.name}${count > 1 ? ` x${count}` : ''}`)
    .join(', ');
  const familyNames = [...families]
    .map((family) => FAMILY_LABEL[family])
    .filter(Boolean)
    .join(', ');
  const body = `${plantCount} ${plantText}, ${families.size} ${familyText}: ${summary}. Families: ${familyNames}.`;

  if (body.length <= MAX_PORTFOLIO_SUMMARY_LENGTH) return body;
  return `${body.slice(0, MAX_PORTFOLIO_SUMMARY_LENGTH - 3)}...`;
}

// The build plan as it was actually played, year by year. The
// flattened `portfolio` loses the ordering, which is most of what a
// plan is — the daily leaderboard shows this so a run can be read as a
// plan rather than a shopping list.
export function createPortfolioByYear(buildPlanByYear = {}) {
  return [1, 2, 3].map((year) => ({
    year,
    plants: Array.isArray(buildPlanByYear?.[year]) ? buildPlanByYear[year] : []
  }));
}

export function createDisasterSummary(yearResults = []) {
  return yearResults.map((result, index) => ({
    year: result?.year ?? index + 1,
    names: (result?.disasters ?? [])
      .map((disaster) => {
        const card = typeof disaster === 'number'
          ? DISASTER_BY_ID[disaster]
          : disaster;
        if (!card) return null;
        const severity = card.severityRoll
          ? ` (${card.severityRoll})`
          : '';
        return `${card.name}${severity}`;
      })
      .filter(Boolean)
  }));
}

export function createSubmissionKey({
  state,
  playerName,
  playerRole,
  playerCountry
}) {
  const finalResult = state?.finalResult ?? {};
  const disasterKey = (state?.yearResults ?? [])
    .map((result, index) => {
      const year = result?.year ?? index + 1;
      const disasters = (result?.disasters ?? [])
        .map((disaster) => {
          if (typeof disaster === 'number') return String(disaster);
          return `${disaster?.id ?? disaster?.name ?? 'unknown'}:${disaster?.severityRoll ?? ''}`;
        })
        .join(',');
      return `${year}:${disasters}`;
    })
    .join('|');

  return [
    state?.seed ?? 'unknown',
    cleanScoreText(playerName),
    cleanScoreText(playerRole),
    cleanScoreText(playerCountry),
    (state?.portfolio ?? []).join(','),
    disasterKey,
    state?.cop30?.id ?? 'unknown',
    Number(finalResult.totalCost ?? 0).toFixed(3)
  ].join('|').slice(0, MAX_SUBMISSION_KEY_LENGTH);
}

export function createScorePayload({
  state,
  playerName,
  playerRole,
  playerCountry,
  expectedCost,
  luckPercentile,
  planRatingVersion
}) {
  const finalResult = state?.finalResult ?? {};
  const mode = state?.mode ?? DEFAULT_GAME_MODE;
  return {
    playerName,
    playerRole: formatPlayerRole(playerRole),
    playerCountry: formatPlayerCountry(playerCountry),
    playerCountryCode: cleanScoreText(playerCountry).toUpperCase(),
    submissionKey: createSubmissionKey({
      state,
      playerName,
      playerRole,
      playerCountry
    }),
    mode,
    gameVersion: GAME_VERSION_BY_MODE[mode] ?? GAME_VERSION,
    totalCost: finalResult.totalCost,
    cop31Outcome: state?.cop30?.id,
    portfolioSummary: createPortfolioSummary(state?.portfolio),
    portfolio: state?.portfolio ?? [],
    portfolioByYear: createPortfolioByYear(state?.buildPlanByYear),
    disasters: createDisasterSummary(state?.yearResults),
    emissions: finalResult.emissions,
    shortfallCost: finalResult.shortfallCost,
    capitalCost: finalResult.capital,
    resourceDevelopmentPremium:
      finalResult.resourceDevelopmentPremium ?? 0,
    cleanEnergyAward: finalResult.cleanEnergyAward,
    resilienceAward: finalResult.resilienceAward,
    affordabilityAward: finalResult.affordabilityAward ?? 0,
    expectedCost,
    luckPercentile,
    // How the expectedCost above was measured. The server ranks Best
    // Plans on the current version only, so entries rated under an
    // older scheme drop off that board instead of being compared with
    // numbers that are not comparable.
    planRatingVersion
  };
}

export async function fetchScores({
  limit = DEFAULT_SCORE_LIMIT,
  mode = DEFAULT_GAME_MODE,
  fetcher = globalThis.fetch
} = {}) {
  assertFetcher(fetcher);

  // One blob key per mode server-side, so the mode has to travel with
  // the read as well as the write. An absent mode reads as Classic.
  const query = `limit=${clampLimit(limit)}&mode=${encodeURIComponent(mode)}`;
  const response = await fetcher(`${SCORE_ENDPOINT}?${query}`, {
    headers: {
      accept: 'application/json'
    }
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw requestError(response, payload);
  }
  const allTimeScores = payload?.allTimeScores ?? payload?.scores;
  const dailyScores = payload?.dailyScores ?? [];
  if (!Array.isArray(allTimeScores) || !Array.isArray(dailyScores)) {
    throw new Error('High scores response was not recognised.');
  }
  const bestPlanAllTimeScores = Array.isArray(payload?.bestPlanAllTimeScores)
    ? payload.bestPlanAllTimeScores
    : [];
  const bestPlanDailyScores = Array.isArray(payload?.bestPlanDailyScores)
    ? payload.bestPlanDailyScores
    : [];
  return {
    dailyScores,
    allTimeScores,
    bestPlanDailyScores,
    bestPlanAllTimeScores,
    scores: allTimeScores
  };
}

export async function submitScore(
  {
    playerName,
    playerRole,
    playerCountry,
    playerCountryCode,
    submissionKey,
    mode = DEFAULT_GAME_MODE,
    gameVersion,
    totalCost,
    cop31Outcome,
    portfolioSummary,
    portfolio,
    portfolioByYear,
    disasters,
    emissions,
    shortfallCost,
    capitalCost,
    resourceDevelopmentPremium,
    cleanEnergyAward,
    resilienceAward,
    affordabilityAward,
    expectedCost,
    luckPercentile,
    planRatingVersion
  },
  { limit = DEFAULT_SCORE_LIMIT, fetcher = globalThis.fetch } = {}
) {
  assertFetcher(fetcher);

  const response = await fetcher(`${SCORE_ENDPOINT}?limit=${clampLimit(limit)}`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json'
    },
    body: JSON.stringify({
      playerName,
      playerRole,
      playerCountry,
      playerCountryCode,
      submissionKey,
      mode,
      gameVersion,
      totalCost,
      cop31Outcome,
      portfolioSummary,
      portfolio,
      portfolioByYear,
      disasters,
      emissions,
      shortfallCost,
      capitalCost,
      resourceDevelopmentPremium,
      cleanEnergyAward,
      resilienceAward,
      affordabilityAward,
      expectedCost,
      luckPercentile,
      planRatingVersion
    })
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw requestError(response, payload);
  }
  if (!payload?.score || !Array.isArray(payload?.scores)) {
    throw new Error('High scores response was not recognised.');
  }
  return payload;
}
